// src/pages/HomePage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, collection, getDocs } from "firebase/firestore";
import { Copy } from "lucide-react";

const HomePage = () => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/signin");
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();

          // Fetch activePackage from subcollection
          const activePkgCol = collection(db, "users", user.uid, "activePackage");
          const activePkgSnap = await getDocs(activePkgCol);
          if (!activePkgSnap.empty) {
            data.activePackage = activePkgSnap.docs[0].data();
          }

          setUserData(data);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  const handleCopyReferral = () => {
    if (userData?.referralId || userData?.referralCode) {
      const code = userData?.referralId || userData?.referralCode;
      navigator.clipboard.writeText(code);
      alert("Referral ID copied ✅");
    }
  };

  if (loading)
    return <p className="text-center py-10 text-gray-600">Loading...</p>;

  return (
    <div className="min-h-screen bg-gray-100 py-10">
      <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col gap-10">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col md:flex-row items-center gap-6">
          <img
            src="/favicon.png"
            alt="Diamond App"
            className="w-28 h-28"
          />
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-800">
              💎 Welcome, {userData?.name || "User"}!
            </h1>
            <p className="text-gray-600 mt-2">
              Your referral ID:{" "}
              <span className="font-mono">{userData?.referralId || userData?.referralCode}</span>
              <button
                onClick={handleCopyReferral}
                className="ml-2 text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Copy size={16} /> Copy
              </button>
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-blue-600">
                ${Number(userData?.totalEarnings || 0).toFixed(3)}
            </p>
            <p className="text-gray-500 mt-1">Total Earnings</p>
          </div>
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-green-600">
              ${userData?.asset || 0}
            </p>
            <p className="text-gray-500 mt-1">Assets</p>
          </div>
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-purple-600">
              {userData?.activePackage?.title || "—"}
            </p>
            <p className="text-gray-500 mt-1">Active Package</p>
          </div>
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-red-600">
              {(userData?.teamA || 0) + (userData?.teamB || 0) + (userData?.teamC || 0)}
            </p>
            <p className="text-gray-500 mt-1">Team Total</p>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          <button
            onClick={() => navigate("/packages")}
            className="bg-blue-600 text-white py-4 rounded-2xl shadow-xl hover:bg-blue-700 transition font-semibold"
          >
            Packages
          </button>
          <button
            onClick={() => navigate("/deposit")}
            className="bg-green-600 text-white py-4 rounded-2xl shadow-xl hover:bg-green-700 transition font-semibold"
          >
            Deposit
          </button>
          <button
            onClick={() => navigate("/withdraw")}
            className="bg-red-600 text-white py-4 rounded-2xl shadow-xl hover:bg-red-700 transition font-semibold"
          >
            Withdraw
          </button>
          <button
            onClick={() => navigate("/daily-claim")}
            className="bg-purple-600 text-white py-4 rounded-2xl shadow-xl hover:bg-purple-700 transition font-semibold"
          >
            Daily Claim
          </button>
          <button
            onClick={() => navigate("/settings")}
            className="bg-gray-600 text-white py-4 rounded-2xl shadow-xl hover:bg-gray-700 transition font-semibold"
          >
            Settings
          </button>
        </div>

        {/* Play Store Section */}
        <div className="flex justify-center mt-10">
  <a
    href="https://play.google.com/store/apps/details?id=com.rewards.originaldiamond"
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 bg-green-600 text-white px-6 py-4 rounded-2xl shadow-xl hover:bg-green-700 transition"
  >
    <img
      src="/play.png"
      alt="Play Store"
      className="h-8 object-contain" // <-- only height, preserve aspect ratio
    />
    <span className="font-semibold text-lg">Play Store</span>
  </a>
</div>

      </div>
    </div>
  );
};

export default HomePage;
